<template>
    <div>
        <app-layout>
            <template #header>
                <AppHead :title="isMtd() ? 'Customer Checkout' : 'Checkout'" />
                <ShopButton :link="route('shop')" :title="isMtd() ? 'Customer Checkout' : 'Checkout'"></ShopButton>
                <flash-messages />
            </template>
            <section class="my-3">
                <p v-if="for_user" class="mb-2 text-secondary"><i class="fas fa-user mr-2"></i>Ordering on behalf of <span
                        v-if="for_user.first_name || for_user.last_name">{{ for_user.first_name }} {{ for_user.last_name
                        }}</span><span v-else>customer,
                        <Phone v-model="for_user.phone_number" />
                        <Email v-model="for_user.email" />
                    </span></p>
                <div class="row">
                    <div class="col-lg-6">
                        <card v-if="errors.length" headerStyle="danger" class="mb-3" icon="fas fa-times-circle"
                            :title="`${errors.length} ${errors.length > 1 ? 'errors' : 'error'} to fix`">
                            <ul class="mb-0 pl-4">
                                <li v-for="message in errors" :key="message">{{ message }}</li>
                            </ul>
                        </card>

                        <card v-if="has_shipping" title="Shipping Address">
                            <div class="address form-check" v-if="mtd_address">
                                <input type="radio" name="ship_address" :id="'address_' + mtd_address"
                                    class="form-check-input" v-bind:value="mtd_address"
                                    @change="selectAddress($event, mtd_address)"
                                    :checked="mtd_address == this.form.ship_address_id" />
                                <label :for="'address_' + mtd_address">Ship to {{ this.$page.props.auth.user.role == 'shop'
                                    ? this.$page.props.auth.user.mtd.company_name : this.$page.props.auth.user.company_name
                                }}
                                </label>
                            </div>
                            <div class="address form-check" v-for="address in addresses" :key="address.id">
                                <input type="radio" name="ship_address" :id="'address_' + address.id"
                                    class="form-check-input" v-bind:value="address.id"
                                    @change="selectAddress($event, address.id)"
                                    :checked="address.id == this.form.ship_address_id" />
                                <label :for="'address_' + address.id">{{ address.address1 }} {{ address.address2 }} {{
                                    address.city }}, {{ address.state }} {{ address.postcode }}
                                </label>
                                <div class="float-right">
                                    <button :disabled="addressProcessing" class="btn btn-link pt-0 text-secondary"
                                        @click="deleteAddress(address)"><i class="fas fa-trash"></i></button>
                                </div>
                            </div>

                            <jet-add-button @click="showAddress" modalTarget="#addressModal">Add Address</jet-add-button>
                        </card>

                        <card v-if="has_mtn_shipping" title="Shipping Method" class="mt-3">
                            <div v-if="!form.ship_address_id"><span>Please select a shipping address</span></div>
                            <div v-else-if="methods.length == 0">
                                <i class="fa fa-spinner fa-spin"></i> <span>Loading shipping rates...</span>
                            </div>
                            <div v-else>
                                <div class="method form-check" v-for="method in methods"
                                    :key="method.service_id + method.label">
                                    <input type="radio" name="ship_method" class="form-check-input" :id="method.service_id"
                                        v-bind:value="method.id" :checked="method.service_id == shipping_method_id"
                                        @change="selectMethod($event, method)" />
                                    <label :for="method.service_id">
                                        <span v-if="method.cost > 0">{{ formatDollars(method.cost) }}</span>
                                        <span v-else>FREE</span> {{ method.label }}
                                    </label>
                                </div>
                            </div>
                        </card>

                        <div class="mt-3">
                            <PaymentMethod class="mt-4" :mtd_instant_payment_only="false" :payment_intents="payment_intents"
                                :paymentBalance="cartTotals.balance"
                                :current_account_balance="cartTotals.current_account_balance" :form="form" :cards="cards"
                                :formBank="formBank" :banks="banks" :link_token="link_token" bankRoute="cart.addBank"
                                :for_user="for_user" :decline_reason="decline_reason" @setPaymentMethod="resetErrors"
                                @setPaymentIntent="resetErrors" @bankAdded="updateSelected" />
                        </div>

                        <card class="mt-2" bodyClasses="p-0">
                            <CheckoutTotals :cartTotals="cartTotals" :wholesale="false" :mtd_disable_tax="mtd_disable_tax"
                                :for_user="for_user" :totalsLoading="totalsLoading" @onUpdate="load" />

                            <div class="d-flex justify-content-end mt-3 p-3">
                                <jet-button class="px-5" btnStyle="success" :disable="form.processing"
                                    @click="completeCheckout()">Place Order</jet-button>
                            </div>
                        </card>

                        <card v-if="errors.length" headerStyle="danger" class="mb-3 mt-3" icon="fas fa-times-circle"
                            :title="`${errors.length} ${errors.length > 1 ? 'errors' : 'error'} to fix`">
                            <ul class="mb-0 pl-4">
                                <li v-for="message in errors" :key="message">{{ message }}</li>
                            </ul>
                        </card>

                    </div>

                    <div class="col-lg-6 mt-3 mt-lg-0">
                        <ShopCheckoutTable :items="items" :isWholesale="false" @onUpdate="load" />
                    </div>
                </div>
            </section>

            <AddCard :routeUrl="route('cart.addCard')" :formCard="formCard" @cardUpdated="updateSelected" />

            <AddShippingAddress ref="addressForm" :states="states" @addAddress="addAddress" :formAddress="formAddress"
                :for_user="for_user" />

            <jet-dialog-modal id="cashModal">
                <template #title>Cash Payment</template>
                <template #content>
                    <i class="fa fa-spinner fa-spin"></i> Waiting for confirmation...
                </template>

                <template #footer>
                    <jet-danger-button class="ml-2" @click.native="cancelCash">Cancel</jet-danger-button>
                </template>
            </jet-dialog-modal>

            <jet-dialog-modal id="termModal">
                <template #title>Payment Term Request</template>
                <template #content>
                    <i class="fa fa-spinner fa-spin"></i> Waiting for confirmation...
                </template>

                <template #footer>
                    <jet-danger-button class="ml-2" @click.native="cancelCash">Cancel</jet-danger-button>
                </template>
            </jet-dialog-modal>

            <jet-dialog-modal id="mtdChckoutModal">
                <template #title>Placing Your Order</template>
                <template #content>
                    {{ this.$page.props.auth.user.mtd.company_name }} has received your request and will place this order on
                    your behalf.
                </template>

                <template #footer>
                    <jet-button class="btn-sm mr-3 my-1 m-w-100" icon="fas fa-times-circle" @click="clearCart">Clear
                        Cart</jet-button>
                </template>
            </jet-dialog-modal>

        </app-layout>
    </div>
</template>


<script>
import AppLayout from '@/Layouts/AppLayout'
import FlashMessages from '@/Shared/FlashMessages'
import JetDialogModal from '@/Jetstream/DialogModal'
import JetDangerButton from '@/Jetstream/DangerButton'
import JetAddButton from '@/Jetstream/AddButton'
import JetInputError from '@/Jetstream/InputError'
import ShopButton from '@/Shared/ShopButton'
import PaymentMethod from '@/Components/Shop/PaymentMethod'
import ShopCheckoutTable from '@/Components/Shop/ShopCheckoutTable'
import CheckoutTotals from '@/Components/Shop/CheckoutTotals'
import AddShippingAddress from '@/Components/Modals/AddShippingAddress'
import AddCard from '@/Components/Modals/AddCard'
import moment from 'moment'
import moment_timezone from 'moment-timezone'

export default {
    components: {
        AppLayout,
        FlashMessages,
        JetDialogModal,
        JetDangerButton,
        JetInputError,
        ShopButton,
        PaymentMethod,
        ShopCheckoutTable,
        CheckoutTotals,
        AddShippingAddress,
        AddCard,
        JetAddButton
    },
    props: {
        items: Array,
        payment_intents: Array,
        payment_intent: String,
        payment_method_id: Number,
        cards: Array,
        banks: Array,
        link_token: String,
        terms: Array,
        addresses: Array,
        ship_address: Object,
        for_user: Object,
        has_shipping: Boolean,
        has_mtn_shipping: Boolean,
        cartTotals: Array,
        states: Array,
        mtd_address: Number,
        mtd_disable_tax: Boolean,
        sales_tax_override_zip_init: String,
    },
    data() {
        return {
            totalsLoading: false,
            cartTotals: this.cartTotals,
            sales_tax_override_zip: this.sales_tax_override_zip_init,
            methods: [],
            shipping_method_id: null,
            errors: [],
            pollInterval: null,
            currentTerms: this.terms,
            selectedTerm: null,
            sales_tax_rate: 0,
            decline_reason: null,
            addressProcessing: false,
            form: this.$inertia.form({
                balance_displayed: this.cartTotals.balance,
                payment_amount: this.cartTotals.balance ?? 0,
                payment_intent: this.payment_intent,
                stripe_payment_method: this.payment_method_id,
                term_id: null,
                ship_address_id: this.ship_address ? this.ship_address.id : null,
                ship_method: this.shipping_method_id,
                user_timezone: null,
                accept_partial: false,
                partial_amount: null,
                cash_credit_amount: null,
                cash_debit_amount: null,
                check_number: null,
            }),
            formCard: this.$inertia.form({
                card_name: null,
                card_number: null,
                card_exp_month: null,
                card_exp_year: null,
                card_cvc: null,
                card_zip: null,
            }),
            formBank: this.$inertia.form({
                public_token: null,
                account_id: null
            }),
            formTermRequest: this.$inertia.form({
                term_id: null
            }),
            formAddress: this.$inertia.form({
                first_name: null,
                last_name: null,
                address1: null,
                address2: null,
                city: null,
                state: null,
                postcode: null,
            }),
            formUpdateAddress: this.$inertia.form({
                address_id: null
            }),
            formUpdateMethod: this.$inertia.form({
                method_id: null
            })
        }
    },
    created() {
        this.moment = moment
    },
    mounted() {
        this.pollInterval = setInterval(this.checkRequests, 10 * 1000);
        this.load()
    },

    beforeUnmount() {
        clearInterval(this.pollInterval)
    },

    methods: {
        loadMethods() {
            if (this.has_shipping) {
                this.totalsLoading = true;
                this.methods = [];
                let component = this;
                axios.get(route('cart.methods', {})).then((response) => {
                    if (response.data.success) {
                        component.methods = response.data.methods;
                        component.shipping_method_id = response.data.shipping_method_id;
                        component.form.ship_method = response.data.shipping_method_id;
                        component.cartTotals = response.data.cartTotals;
                        component.sales_tax_rate = response.data.sales_tax_rate;
                        component.sales_tax_override_zip = response.data.sales_tax_override_zip;
                        this.totalsLoading = false;
                    }
                });
            }
        },
        loadTotals() {
            this.totalsLoading = true;
            let component = this;
            axios.get(route('cart.totals', {})).then((response) => {
                if (response.data.success) {
                    component.cartTotals = response.data.cartTotals;
                    component.sales_tax_rate = response.data.sales_tax_rate;
                    component.sales_tax_override_zip = response.data.sales_tax_override_zip;
                    this.totalsLoading = false;
                }
            });
        },
        load() {
            this.has_mtn_shipping ? this.loadMethods() : this.loadTotals()
        },
        resetErrors() {
            this.decline_reason = null;
            this.errors = [];
        },
        async addAddress() {
            this.formAddress.post(this.route('cart.addAddress'), {
                preserveScroll: true,
                onSuccess: () => {
                    $('#addressModal').modal('hide')
                    this.form.ship_address_id = this.ship_address.id
                    this.loadMethods()
                    this.$refs.addressForm.$refs.address1.$el.value = null
                    this.formAddress.reset()
                }
            })
        },
        showAddress() {
            this.resetErrors();
        },
        deleteAddress(address) {
            this.addressProcessing = true
            this.$inertia.visit(this.route('cart.deleteAddress', address), {
                onSuccess: () => this.addressProcessing = false,
                onFinish: () => this.addressProcessing = false,
                preserveScroll: true
            })
        },
        selectAddress(e, address_id) {
            this.totalsLoading = true;
            this.resetErrors();
            if (e.target.checked) this.form.ship_address_id = address_id;
            this.formUpdateAddress.address_id = address_id;

            this.formUpdateAddress.post(this.route('cart.updateAddress'), {
                preserveScroll: true,
                onSuccess: () =>
                    this.loadMethods()
            });
        },
        selectMethod(e, method) {
            this.totalsLoading = true;
            this.form.ship_method = method.service_id;
            this.formUpdateMethod.method_id = method.service_id;

            this.formUpdateMethod.post(this.route('cart.updateMethod'), {
                preserveScroll: true,
                onSuccess: () =>
                    this.loadTotals()
            });
        },
        completeCheckout() {
            this.resetErrors();
            let canCheckout = false;

            if (this.cartTotals.total == 0 || this.cartTotals.balance == 0) {
                canCheckout = true;
            } else if ((this.form.payment_intent == 'Credit Card' ||
                this.form.payment_intent == 'ACH') &&
                !this.form.stripe_payment_method) {
                this.errors[this.errors.length] = "Please select a payment method.";
            } else if (this.$page.props.auth.user.role != 'shop' && this.form.payment_intent == 'Cash') {
                if (!this.$page.props.auth.user.drawer) {
                    this.errors[this.errors.length] = "You must open a cash drawer before accepting cash or check payments.";
                    canCheckout = false;
                } else {
                    let amount = (this.form.accept_partial && this.form.partial_amount > 0) ? this.form.partial_amount : this.order_total;
                    if (!this.form.cash_credit_amount || parseFloat(this.form.cash_credit_amount) == 0) {
                        this.errors[this.errors.length] = "Please enter customer's cash payment amount (tendered).";
                        canCheckout = false;
                    } else if (parseFloat(this.form.cash_credit_amount) < parseFloat(amount)) {
                        this.errors[this.errors.length] = "Cash payment amount (tendered) must be at least due amount. Lower amount due can be entered as partial payment.";
                        canCheckout = false;
                    } else {
                        canCheckout = true;
                    }
                }
            } else {
                canCheckout = true;
            }

            if (this.has_shipping) {
                if (!this.form.ship_address_id) {
                    this.errors[this.errors.length] = "Please select a shipping address.";
                    canCheckout = false;
                } else if (this.has_mtn_shipping) {
                    if (!this.form.ship_method) {
                        this.errors[this.errors.length] = "Please select a shipping method.";
                        canCheckout = false;
                    }
                }
            }
            if (canCheckout) {
                this.form.user_timezone = this.moment.tz.guess();
                this.form.balance_displayed = this.cartTotals.balance;

                if (this.$page.props.auth.user.role == 'shop' && this.form.payment_intent == 'Cash') {
                    $('#cashModal').modal('show');

                    axios.post(route('cart.requestTerm'), {
                        payment_intent: this.form.payment_intent,
                    }).then((response) => {
                    });

                } else if (this.$page.props.auth.user.role == 'shop' && this.form.payment_intent == 'Account Balance') {
                    $('#termModal').modal('show');
                    axios.post(route('cart.requestTerm'), {
                        payment_intent: this.form.payment_intent,
                    }).then((response) => {
                    });
                } else {
                    this.form.post(this.route('cart.order'), {
                        preserveScroll: true,
                        onSuccess: () => {
                            this.load();
                        }
                    });
                }
            }
        },
        checkRequests() {
            let that = this;
            axios.post(route('cart.checkRequests'), {
            }).then((response) => {
                if (response.data.order_id) {
                    $('#cashModal').modal('hide');
                    $('#termModal').modal('hide');

                    this.$inertia.visit(this.route('order.view', response.data.order_id));
                } else if (response.data.request &&
                    response.data.request.request_status == "declined" &&
                    this.form.payment_intent == response.data.request.type) {
                    $('#termModal').modal('hide');

                    this.decline_reason = response.data.request.decline_reason;
                } else if (response.data.request &&
                    response.data.request.request_status == "canceled" &&
                    this.form.payment_intent == response.data.request.type) {
                    $('#termModal').modal('hide');
                    $('#mtdChckoutModal').modal('show');

                } else {
                    this.currentTerms = response.data.terms;
                    if (this.selectedTerm) {
                        for (var i = 0; i < this.currentTerms.length; i++) {
                            if (this.currentTerms[i].id == this.selectedTerm.id) {
                                if (this.currentTerms[i].request_status == "approved") {
                                    $('#termModal').modal('hide');
                                    this.form.term_id = '' + this.selectedTerm.id;

                                    this.form.post(this.route('cart.order'));
                                } else if (this.currentTerms[i].request_status == "declined") {
                                    $('#termModal').modal('hide');
                                }
                            }
                        }
                    }
                }
            });
        },
        updateSelected() {
            this.form.stripe_payment_method = this.payment_method_id;
        },

        cancelCash() {
            axios.post(route('cart.requestCancel'), {
            }).then((response) => {
                $('#termModal').modal('hide');
                $('#cashModal').modal('hide');
            });
        },
        clearCart() {
            $('#termModal').modal('hide');
            $('#cashModal').modal('hide');
            this.$inertia.visit(route('cart.clear'));
        },
    },
}
</script>
